import CarouselAdBanner from 'elements/CarouselAdBanner'
import { AVAILABILITY_FILTER_KEY_V2 } from 'elements/hardwareFilter/stockAvailability/AvailabilityFilterNodeFactoryV2'
import { DistributorTradePricingContext } from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import { ComponentVersionsInherit, ComponentVersions_3_0, Theme, styled, useMediaQuery } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import useDistributorTradePricing from 'services/useDistributorTradePricing'
import { ActionType } from 'types/tracking'
import { useFeatureFlag } from 'util/featureFlags'
import { orgSelectors } from '../../../ducks/orgs'
import { DISTRIBUTOR_FILTER_KEY_V2 } from '../../../elements/hardwareFilter/distributor/constants'
import useGroupedHardwareSelector from '../../../elements/hardwareSelectorV2/fetch/useGroupHardwareSelector'
import listQueryParamsFormatter from '../../../elements/input/listQueryParamsFormatter'
import ConnectionBanner from '../banner/ConnectionBanner'
import { useShopCategoryConfig } from '../categoryConfig'
import MarketplaceDisclaimerBanner from '../elements/MarketplaceDisclaimerBanner'
import NoResultFoundPage from '../elements/NoResultFoundPage'
import ShopPageContainer, { SHOP_PAGE_SURFACE_COLOR } from '../elements/ShopPageContainer'
import useEnabledHardwareSuppliers from '../hooks/useEnabledHardwareSuppliers'
import { useRecommendedProductsFromDesigns } from '../hooks/useRecommendedProductsFromDesigns'
import { useRecommendedProductsFromPurchases } from '../hooks/useRecommendedProductsFromPurchases'
import { HardwareSupplierFilterKeyType } from '../type'
import CollectionBlock from './CollectionBlock'
import DealsCarousel from './DealsCarousel'
import ProductGrid from './ProductGrid'
import ShopByCategory from './ShopByCategory'
import SupplierCategoryCarousels from './SupplierCategoryCarousels'
import useHomeProductCarouselData from './useHomeProductCarouselData'

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

const WhiteContainer = styled('div')({
  background: 'white',
})

interface HomeCatalogMultiDistributorProps {
  distributor: HardwareSupplierFilterKeyType | undefined
  hideStandardCategorySections?: boolean
  showSupplierCategoryCarousels?: boolean
}

const HomeCatalogMultiDistributor = ({
  distributor,
  hideStandardCategorySections = false,
  showSupplierCategoryCarousels = false,
}: HomeCatalogMultiDistributorProps) => {
  const translate = useTranslate()
  const orgId = useSelector(orgSelectors.getOrg)?.id || 0
  const countryIso2 = useSelector(orgSelectors.getOrgIso2) || ''
  const { carouselEntries, isLoading: carouselLoading } = useHomeProductCarouselData(countryIso2)
  const enableShopUplift = useFeatureFlag('shop_uplift', 'on')
  const { getCategoryByComponentType } = useShopCategoryConfig()

  const defaultFilterValues = useMemo(() => {
    return distributor
      ? {
          [DISTRIBUTOR_FILTER_KEY_V2]: listQueryParamsFormatter.format({ value: new Set([distributor]) }),
          [AVAILABILITY_FILTER_KEY_V2]: 'true',
        }
      : { [AVAILABILITY_FILTER_KEY_V2]: 'true' }
  }, [distributor])

  const {
    modules,
    inverters,
    batteries,
    mounting,
    moduleLoading,
    moduleLoaded,
    inverterLoading,
    inverterLoaded,
    batteryLoading,
    batteryLoaded,
    mountingLoading,
    mountingLoaded,
    pageSize,
  } = useGroupedHardwareSelector(defaultFilterValues)

  const { data: designRecommendations, isLoading: designRecommendationsLoading } = useRecommendedProductsFromDesigns({
    orgId,
    inStock: true,
    limit: 20,
    timeframeDays: 90,
  })

  const { data: purchaseRecommendations, isLoading: purchaseRecommendationsLoading } =
    useRecommendedProductsFromPurchases({
      orgId,
      inStock: true,
      limit: 20,
      timeframeDays: 90,
    })

  const designRecommendationItems = designRecommendations?.recommended_products || []
  const purchaseRecommendationItems = purchaseRecommendations?.recommended_products || []
  const showDesignRecommendations = designRecommendationsLoading || designRecommendationItems.length > 0
  const showPurchaseRecommendations = purchaseRecommendationsLoading || purchaseRecommendationItems.length > 0

  const RECOMMENDATION_CAROUSEL_LIMIT = 10
  const designsViewAllLink =
    designRecommendationItems.length > RECOMMENDATION_CAROUSEL_LIMIT ? '/shop/recommendations/designs' : undefined
  const purchasesViewAllLink =
    purchaseRecommendationItems.length > RECOMMENDATION_CAROUSEL_LIMIT ? '/shop/recommendations/purchases' : undefined

  const noResults =
    modules?.length === 0 && inverters?.length === 0 && batteries?.length === 0 && mounting?.length === 0
  const allRowsLoaded = moduleLoaded && inverterLoaded && batteryLoaded && mountingLoaded
  const anyRowStillFetching = moduleLoading || inverterLoading || batteryLoading || mountingLoading

  if (!showSupplierCategoryCarousels && allRowsLoaded && noResults && !anyRowStillFetching) {
    return <NoResultFoundPage text="No results found" />
  }

  const moduleCategory = getCategoryByComponentType('module')
  const inverterCategory = getCategoryByComponentType('inverter')
  const batteryCategory = getCategoryByComponentType('battery')
  const otherCategory = getCategoryByComponentType('other')

  const panelLink = distributor
    ? `/shop/search/${moduleCategory?.searchKey || 'modules'}/supplier/${distributor}`
    : `/shop/search/${moduleCategory?.searchKey || 'modules'}`
  const inverterLink = distributor
    ? `/shop/search/${inverterCategory?.searchKey || 'inverters'}/supplier/${distributor}`
    : `/shop/search/${inverterCategory?.searchKey || 'inverters'}`
  const batteryLink = distributor
    ? `/shop/search/${batteryCategory?.searchKey || 'batteries'}/supplier/${distributor}`
    : `/shop/search/${batteryCategory?.searchKey || 'batteries'}`
  const otherLink = distributor
    ? `/shop/search/${otherCategory?.searchKey || 'others'}/supplier/${distributor}`
    : `/shop/search/${otherCategory?.searchKey || 'others'}`

  return (
    <Container>
      <DealsCarousel distributor={distributor} />
      {enableShopUplift && !distributor && showDesignRecommendations && (
        <ProductGrid
          carouselMode={true}
          componentsData={designRecommendationItems}
          title={translate('From Your Designs')}
          description={translate('Recommended based on your current projects')}
          viewAllLink={designsViewAllLink}
          category="from-your-designs"
          loading={designRecommendationsLoading}
          pageSize={RECOMMENDATION_CAROUSEL_LIMIT}
        />
      )}

      {enableShopUplift && !distributor && showPurchaseRecommendations && (
        <ProductGrid
          carouselMode={true}
          componentsData={purchaseRecommendationItems}
          title={translate('From Your Past Purchases')}
          description={translate("Reorder items you've bought before")}
          viewAllLink={purchasesViewAllLink}
          category="from-your-past-purchases"
          loading={purchaseRecommendationsLoading}
          pageSize={RECOMMENDATION_CAROUSEL_LIMIT}
        />
      )}

      {enableShopUplift &&
        !distributor &&
        carouselEntries.map((entry) => (
          <ProductGrid
            key={entry.config.order}
            carouselMode={true}
            componentsData={entry.componentsData}
            title={entry.config.title}
            description={entry.config.description}
            viewAllLink={entry.viewAllLink}
            category="from-shop-product-carousel-control"
            loading={carouselLoading}
            pageSize={10}
          />
        ))}

      {showSupplierCategoryCarousels && distributor && <SupplierCategoryCarousels distributor={distributor} />}

      {!hideStandardCategorySections && !showSupplierCategoryCarousels && (
        <>
          <ProductGrid
            carouselMode={true}
            componentsData={modules}
            title={translate(`Shop ${moduleCategory?.name || 'Panels'}`)}
            viewAllLink={panelLink}
            category="module"
            loading={moduleLoading && !moduleLoaded}
            isFetching={moduleLoading}
            pageSize={pageSize}
          />
          <ProductGrid
            carouselMode={true}
            componentsData={inverters}
            title={translate(`Shop ${inverterCategory?.name || 'Inverters'}`)}
            viewAllLink={inverterLink}
            category="inverter"
            loading={inverterLoading && !inverterLoaded}
            isFetching={inverterLoading}
            pageSize={pageSize}
          />
          <ProductGrid
            carouselMode={true}
            componentsData={batteries}
            title={translate(`Shop ${batteryCategory?.name || 'Batteries'}`)}
            viewAllLink={batteryLink}
            category="battery"
            loading={batteryLoading && !batteryLoaded}
            isFetching={batteryLoading}
            pageSize={pageSize}
          />
          <ProductGrid
            carouselMode={true}
            componentsData={mounting}
            title={translate(`Shop ${otherCategory?.name || 'Mounting + BOS'}`)}
            viewAllLink={otherLink}
            category="mounting+bos"
            loading={mountingLoading && !mountingLoaded}
            isFetching={mountingLoading}
            pageSize={pageSize}
          />
        </>
      )}
    </Container>
  )
}

const OrderingHome = ({ distributor }: { distributor?: HardwareSupplierFilterKeyType }) => {
  const enabledHardwareShopV3 = useFeatureFlag('enable_multi_distributor_shop', 'on')
  const enableShopUplift = useFeatureFlag('shop_uplift', 'on')
  const { filteredSuppliers: hardwareDistributors } = useEnabledHardwareSuppliers()
  const { enabledCategories, hasCustomConfig } = useShopCategoryConfig()
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'ordering_home',
    eventName: 'Hardware Navigation Button',
  })

  const { distributorTradePricing, isChecking } = useDistributorTradePricing({
    orgId: useSelector(orgSelectors.getOrg)?.id || 0,
    distributors: hardwareDistributors,
  })
  const isMdScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  // Only show connect banner CarouselAdBanner if :
  // 1. enable_multi_distributor_shop feature flag is enabled
  // 2. Is on multi distributor page. if there is a distributor, that means the page is on Shop by distributor
  const showConnectionBanner = enabledHardwareShopV3 && !distributor && !enableShopUplift
  const useConfigDrivenCategories = enableShopUplift && hasCustomConfig
  const hideStandardCategorySections = useConfigDrivenCategories
  const showSupplierCategoryCarousels = useConfigDrivenCategories && Boolean(distributor)

  useEffect(() => {
    if (enabledHardwareShopV3 && distributor) {
      trackUserInteraction({ type: ActionType.CLICK }, { source: 'lhs_home_button', distributor })
    } else if (!enabledHardwareShopV3) {
      trackUserInteraction({ type: ActionType.CLICK }, { page: 'hardware_home' })
    }
  }, [distributor])

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <DistributorTradePricingContext.Provider value={{ distributorTradePricing, isChecking }}>
        <ShopPageContainer
          surfaceColor={enabledHardwareShopV3 ? undefined : null}
          disableVerticalPadding
          data-testid="legacy-shop-home"
        >
          {enabledHardwareShopV3 && (
            <WhiteContainer>
              {enableShopUplift && <CarouselAdBanner formats={['billboard_carousel']} placement="home_top" />}
              {showConnectionBanner ? <ConnectionBanner /> : null}
            </WhiteContainer>
          )}
          {enabledHardwareShopV3 && (
            <HomeCatalogMultiDistributor
              distributor={distributor}
              hideStandardCategorySections={hideStandardCategorySections}
              showSupplierCategoryCarousels={showSupplierCategoryCarousels}
            />
          )}
          {enabledHardwareShopV3 && !distributor && <ShopByCategory distributor={distributor} />}
          {!enabledHardwareShopV3 &&
            enabledCategories.map((category) => (
              <CollectionBlock
                trackSource="hardware_home"
                key={category.id}
                title={category.name}
                componentType={category.componentType}
                viewMoreLink={`/shop/search/${category.searchKey}`}
              />
            ))}
          {enableShopUplift && (
            <CarouselAdBanner
              formats={['billboard_carousel']}
              placement="home_bottom"
              containerStyle={{ marginBottom: 20 }}
              showIndicators={false}
              maxSlides={1}
            />
          )}
        </ShopPageContainer>
        <div style={{ marginBottom: isMdScreen ? '36px' : '0px', width: '100%', background: SHOP_PAGE_SURFACE_COLOR }}>
          <ShopPageContainer surfaceColor={null} disableVerticalPadding>
            <MarketplaceDisclaimerBanner scrollIntoViewOnExpand />
          </ShopPageContainer>
        </div>
      </DistributorTradePricingContext.Provider>
    </ComponentVersionsInherit>
  )
}

export default OrderingHome
